import { Controller } from "@hotwired/stimulus"

// Checkbox dropdown for multi-select filters (Cup::DataTable::MultiSelect).
// - click trigger → toggle panel open
// - click outside or Escape → close
// - each checkbox change → update the trigger label counter
// - on close (if dirty): submit the surrounding form (one request per batch)
//
// Ported from Werner's multi_select_controller.js. All user-facing copy comes
// from i18n via data-values (defaultLabel + countTemplate); no hardcoded strings.
export default class extends Controller {
  static targets = ["trigger", "panel", "checkbox", "label"]
  static values  = { defaultLabel: String, countTemplate: String }

  connect() {
    this.boundOnDocClick = this.onDocClick.bind(this)
    this.boundOnEsc      = this.onEsc.bind(this)
    this.dirty = false
    this.updateLabel()
  }

  disconnect() {
    document.removeEventListener("click", this.boundOnDocClick)
    document.removeEventListener("keydown", this.boundOnEsc)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    const open = this.panelTarget.classList.toggle("hidden") === false
    if (open) {
      document.addEventListener("click", this.boundOnDocClick)
      document.addEventListener("keydown", this.boundOnEsc)
    } else {
      this.close()
    }
  }

  onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onEsc(event) {
    if (event.key === "Escape") this.close()
  }

  close() {
    if (!this.panelTarget.classList.contains("hidden")) {
      this.panelTarget.classList.add("hidden")
    }
    document.removeEventListener("click", this.boundOnDocClick)
    document.removeEventListener("keydown", this.boundOnEsc)
    if (this.dirty) {
      this.dirty = false
      this.element.closest("form")?.requestSubmit()
    }
  }

  change() {
    this.dirty = true
    this.updateLabel()
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    this.dirty = true
    this.updateLabel()
  }

  updateLabel() {
    if (!this.hasLabelTarget) return
    const checked = this.checkboxTargets.filter(cb => cb.checked)
    if (checked.length === 0) {
      this.labelTarget.textContent = this.defaultLabelValue
    } else if (checked.length === 1) {
      const lab = checked[0].closest("label")?.querySelector("[data-label]")?.textContent?.trim() || checked[0].value
      this.labelTarget.textContent = lab
    } else {
      this.labelTarget.textContent = this.countLabel(checked.length)
    }
  }

  // Interpolates %{count} in the i18n template passed via data-value.
  countLabel(count) {
    const template = this.countTemplateValue || "%{count}"
    return template.replace("%{count}", count)
  }
}
